import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { ToWords } from "to-words";
import { Invoice } from "../../../generated/graphql";
import { useMemo } from "react";

const InvoicePaymentInfo: React.FC<{
  invoice: Invoice;
}> = ({ invoice }) => {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const items = useMemo(() => {
    return [
      {
        label: "Company Name",
        value: invoice?.paymentDetail?.companyName,
      },
      {
        label: "Branch Name",
        value: invoice?.paymentDetail?.branchName,
      },
      {
        label: "Bank No",
        value: invoice?.paymentDetail?.bankNo,
      },
      {
        label: "Swift Code",
        value: invoice?.paymentDetail?.swiftCode,
      },
      {
        label: "Account No. / IBAN",
        value: invoice?.paymentDetail?.accountNo,
      },
    ];
  }, [invoice]);

  const toWords = new ToWords({
    localeCode: "en-IN",
    converterOptions: {
      currency: true,
      ignoreDecimal: false,
      ignoreZeroCurrency: false,
      doNotAddOnly: true,
      currencyOptions: {
        name: "Dollar",
        plural: "Dollars",
        symbol: "$",
        fractionalUnit: {
          name: "Cent",
          plural: "Cents",
          symbol: "",
        },
      },
    },
  });

  return (
    <View style={styles.container}>
      <View style={[styles.col]}>
        <View style={styles.inWordsContainer}>
          <Text
            style={[styles.inWordsText, { fontWeight: 600, marginBottom: 2 }]}
          >
            In words:
          </Text>
          <Text style={styles.inWordsText}>
            {toWords.convert(invoice?.total || 0)}
          </Text>
        </View>
        <Text style={styles.title}>Payment Details</Text>
        <View>
          {items.map((elm, index) => (
            <View key={index} style={styles.paymentDetailItem}>
              <Text style={[styles.paymentInfoLabel, { width: 76 }]}>
                {elm.label}
              </Text>
              <Text style={styles.paymentInfoLabel}>:</Text>
              <Text
                style={[
                  styles.paymentInfoLabel,
                  { flex: 1, textAlign: "right", fontWeight: 600 },
                ]}
              >
                {elm.value}
              </Text>
            </View>
          ))}
        </View>
      </View>
      <View style={[styles.col]}>
        <View style={styles.subtotalItem}>
          <Text style={[styles.subtotalItemText]}>Subtotal</Text>
          <Text style={[styles.subtotalItemText, { textAlign: "center" }]}>
            $ {formatter.format(invoice?.subTotal)}
          </Text>
        </View>
        <View style={styles.subtotalItem}>
          <Text style={[styles.subtotalItemText]}>Eddition Costa</Text>
          <Text style={[styles.subtotalItemText, { textAlign: "center" }]}>
            ${" "}
            {invoice?.additionalCost
              ? formatter.format(invoice?.additionalCost)
              : 0}
          </Text>
        </View>
        <View style={styles.totalContainer}>
          <Text style={[styles.totalText, { fontWeight: 600 }]}>NTA</Text>
          <Text style={[styles.totalText, { textAlign: "center" }]}>
            $ {formatter.format(invoice?.total)}
          </Text>
        </View>
      </View>
    </View>
  );
};

// Create styles
const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
    fontFamily: "Poppins",
  },
  col: {
    flex: 1,
  },
  title: {
    fontSize: 12,
    textTransform: "uppercase",
    fontWeight: 600,
    marginBottom: 4,
  },
  paymentDetailItem: {
    display: "flex",
    flexDirection: "row",
  },
  paymentInfoLabel: {
    fontSize: "8",
  },
  totalContainer: {
    backgroundColor: "#2fd5ee",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  totalText: {
    color: "#fff",
    flex: 1,
    fontSize: 10,
    textTransform: "uppercase",
  },
  inWordsContainer: {
    marginBottom: 21,
  },
  inWordsText: {
    fontSize: 8,
  },
  subtotalItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 16,
    paddingRight: 16,
  },
  subtotalItemText: {
    fontSize: 10,
    fontWeight: "bold",
    flex: 1,
  },
});

export default InvoicePaymentInfo;
